/**
 * /knowledge-base/:id — Article reader (Milestone 1).
 *
 * Renders the HTML body, shows metadata, tracks views, manager+ sees Edit/Archive.
 * Images embedded as <img src="/api/kb-files/…"> are auto-rewritten with ?token=.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Edit, ArchiveX, Eye, Clock, User, Tag, BookOpen,
  AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import KbVoteButtons from "@/components/kb/KbVoteButtons";

export default function KnowledgeBaseArticle() {
  const { id } = useParams();
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasPerm("kb.edit");
  const canReview = hasPerm("kb.mark_reviewed");
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/kb/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => toast.error("Failed to load article"))
      .finally(() => setLoading(false));
  }, [id]);

  // Rewrite KB file URLs to include the token (for <img src> auth)
  const html = useMemo(() => {
    const raw = article?.body_md || article?.content || "";
    if (!raw) return "";
    const token = localStorage.getItem("kippu_token") || "";
    return raw.replace(
      /<img\s+([^>]*?)src=["']\/api\/kb-files\/([\w-]+)["']/g,
      (_m, attrs, fid) => `<img ${attrs}src="/api/kb-files/${fid}?token=${encodeURIComponent(token)}"`,
    );
  }, [article]);

  if (loading) return <div className="text-sm text-slate-400 py-12 text-center">Loading…</div>;
  if (!article) return <div className="text-sm text-slate-400 py-12 text-center">Article not found.</div>;

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="kb-article-page">
      <div className="flex items-center justify-between">
        <Link to="/knowledge-base" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4 mr-1" /> Knowledge Base
        </Link>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/knowledge-base/${id}/edit`)}
                    data-testid="kb-edit-btn">
              <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        )}
      </div>

      {article.status === "archived" && (
        <div className="rounded-md bg-slate-100 border border-slate-200 px-4 py-2 text-xs text-slate-600 flex items-center gap-2"
             data-testid="kb-archived-banner">
          <ArchiveX className="w-3.5 h-3.5" />
          This article is archived. It won't appear in the default list.
        </div>
      )}
      {article.status === "draft" && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800 flex items-center gap-2"
             data-testid="kb-draft-banner">
          <Edit className="w-3.5 h-3.5" />
          Draft — not visible to other agents in default searches until you publish it.
        </div>
      )}
      {(() => {
        const refIso = article.last_reviewed_at || article.updated_at;
        if (!refIso) return null;
        const daysOld = Math.floor((Date.now() - new Date(refIso).getTime()) / 86400000);
        if (daysOld < 83) return null;   // only show within 7-day-warning window
        const overdue = daysOld >= 90;
        return (
          <div className={`rounded-md border px-4 py-2 text-xs flex items-center justify-between gap-2 ${
            overdue ? "bg-rose-50 border-rose-200 text-rose-800"
                    : "bg-amber-50 border-amber-200 text-amber-800"
          }`} data-testid="kb-stale-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span>
                {overdue
                  ? `Review overdue — last reviewed ${daysOld} days ago.`
                  : `Approaching review due-date (${90 - daysOld} day${90 - daysOld === 1 ? "" : "s"} left).`}
              </span>
            </div>
            {canReview && (
              <Button size="sm" variant="outline"
                      className={`h-7 text-[11px] ${overdue ? "border-rose-300 text-rose-700 hover:bg-rose-100" : "border-amber-300 text-amber-700 hover:bg-amber-100"}`}
                      onClick={async () => {
                        try {
                          await api.post(`/kb/${article.id}/mark-reviewed`);
                          toast.success("Marked as reviewed");
                          const r = await api.get(`/kb/${article.id}`);
                          setArticle(r.data);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      }}
                      data-testid="kb-mark-reviewed-btn">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark as reviewed
              </Button>
            )}
          </div>
        );
      })()}

      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-[11px] text-slate-500 uppercase tracking-wider">
              <BookOpen className="w-3 h-3" />
              {article.category || "Uncategorized"}
            </div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900" data-testid="kb-article-title">
              {article.title}
            </h1>
            {article.summary && (
              <p className="text-sm text-slate-600">{article.summary}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 pt-1">
              <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {article.owner_name || article.owner_email || "system"}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Updated {new Date(article.updated_at).toLocaleString()}</span>
              <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {article.view_count || 0} views</span>
              {(article.tags || []).length > 0 && (
                <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" />
                  {article.tags.map((t) => (
                    <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] ml-0.5">{t}</Badge>
                  ))}
                </span>
              )}
            </div>
          </div>

          <hr className="border-slate-200" />

          <div className="prose prose-sm max-w-none kb-article-body"
               dangerouslySetInnerHTML={{ __html: html }}
               data-testid="kb-article-body" />

          <KbVoteButtons
            articleId={article.id}
            initialHelpful={article.helpful_count || 0}
            initialUnhelpful={article.unhelpful_count || 0}
          />
        </CardContent>
      </Card>
    </div>
  );
}
